import Phaser from "phaser";
import type { MapDefinition } from "@ninja/game-content";
import { transformTileCanvas } from "./transformTileCanvas.js";

interface TerrainPlacement {
  assetKey: string;
  x: number;
  y: number;
  width: number;
  height: number;
  opacity: number;
  flipHorizontal: boolean;
  flipVertical: boolean;
  flipDiagonal: boolean;
}

interface RenderedChunk {
  textureKey: string;
  image: Phaser.GameObjects.Image;
}

const CHUNK_SIZE = 512;
const VIEW_MARGIN_CHUNKS = 1;

export class TerrainChunkRenderer {
  private readonly assetsByKey: ReadonlyMap<
    string,
    MapDefinition["terrainAssets"][number]
  >;
  private readonly placementsByDepth = new Map<
    number,
    Map<string, TerrainPlacement[]>
  >();
  private readonly renderedChunks = new Map<string, RenderedChunk>();
  private readonly transformedSources = new Map<
    string,
    HTMLCanvasElement
  >();

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly map: MapDefinition,
  ) {
    this.assetsByKey = new Map(
      map.terrainAssets.map((asset) => [asset.assetKey, asset]),
    );
    this.indexPlacements();
  }

  sync(): void {
    const camera = this.scene.cameras.main;
    const view = camera.worldView;
    const mapWidth = this.map.width * this.map.tileSize;
    const mapHeight = this.map.height * this.map.tileSize;
    const minChunkX = this.clampChunk(
      Math.floor(Math.max(0, view.x) / CHUNK_SIZE) - VIEW_MARGIN_CHUNKS,
      Math.ceil(mapWidth / CHUNK_SIZE),
    );
    const maxChunkX = this.clampChunk(
      Math.floor(Math.min(mapWidth, view.right) / CHUNK_SIZE) +
        VIEW_MARGIN_CHUNKS,
      Math.ceil(mapWidth / CHUNK_SIZE),
    );
    const minChunkY = this.clampChunk(
      Math.floor(Math.max(0, view.y) / CHUNK_SIZE) - VIEW_MARGIN_CHUNKS,
      Math.ceil(mapHeight / CHUNK_SIZE),
    );
    const maxChunkY = this.clampChunk(
      Math.floor(Math.min(mapHeight, view.bottom) / CHUNK_SIZE) +
        VIEW_MARGIN_CHUNKS,
      Math.ceil(mapHeight / CHUNK_SIZE),
    );
    const desired = new Set<string>();

    for (const depth of this.placementsByDepth.keys())
      for (let chunkY = minChunkY; chunkY <= maxChunkY; chunkY += 1)
        for (let chunkX = minChunkX; chunkX <= maxChunkX; chunkX += 1) {
          const key = this.chunkKey(depth, chunkX, chunkY);
          const placements = this.placementsByDepth
            .get(depth)
            ?.get(this.positionKey(chunkX, chunkY));
          if (!placements?.length) continue;
          desired.add(key);
          if (!this.renderedChunks.has(key))
            this.renderedChunks.set(
              key,
              this.createChunk(depth, chunkX, chunkY, placements),
            );
        }

    for (const [key, chunk] of this.renderedChunks) {
      if (desired.has(key)) continue;
      chunk.image.destroy();
      this.scene.textures.remove(chunk.textureKey);
      this.renderedChunks.delete(key);
    }
  }

  dispose(): void {
    for (const chunk of this.renderedChunks.values()) {
      chunk.image.destroy();
      this.scene.textures.remove(chunk.textureKey);
    }
    this.renderedChunks.clear();
    this.transformedSources.clear();
  }

  private indexPlacements(): void {
    const chunkColumns = Math.ceil(
      (this.map.width * this.map.tileSize) / CHUNK_SIZE,
    );
    const chunkRows = Math.ceil(
      (this.map.height * this.map.tileSize) / CHUNK_SIZE,
    );

    for (const layer of this.map.tileLayers) {
      let chunks = this.placementsByDepth.get(layer.depth);
      if (!chunks) {
        chunks = new Map();
        this.placementsByDepth.set(layer.depth, chunks);
      }

      for (const span of layer.spans) {
        const asset = this.assetsByKey.get(span.assetKey);
        if (!asset) continue;
        const width = span.flipDiagonal ? asset.height : asset.width;
        const height = span.flipDiagonal ? asset.width : asset.height;
        for (let offset = 0; offset < span.length; offset += 1) {
          const tileX = (span.startColumn + offset) * this.map.tileSize;
          const tileY = span.row * this.map.tileSize;
          const placement: TerrainPlacement = {
            assetKey: asset.assetKey,
            x: tileX + layer.offsetX + asset.tileOffsetX,
            y:
              tileY +
              this.map.tileSize -
              height +
              layer.offsetY +
              asset.tileOffsetY,
            width,
            height,
            opacity: layer.opacity,
            flipHorizontal: span.flipHorizontal,
            flipVertical: span.flipVertical,
            flipDiagonal: span.flipDiagonal,
          };
          const firstX = Math.max(0, Math.floor(placement.x / CHUNK_SIZE));
          const lastX = Math.min(
            chunkColumns - 1,
            Math.floor((placement.x + width - 1) / CHUNK_SIZE),
          );
          const firstY = Math.max(0, Math.floor(placement.y / CHUNK_SIZE));
          const lastY = Math.min(
            chunkRows - 1,
            Math.floor((placement.y + height - 1) / CHUNK_SIZE),
          );
          for (let chunkY = firstY; chunkY <= lastY; chunkY += 1)
            for (let chunkX = firstX; chunkX <= lastX; chunkX += 1) {
              const key = this.positionKey(chunkX, chunkY);
              const placements = chunks.get(key) ?? [];
              placements.push(placement);
              chunks.set(key, placements);
            }
        }
      }
    }
  }

  private createChunk(
    depth: number,
    chunkX: number,
    chunkY: number,
    placements: readonly TerrainPlacement[],
  ): RenderedChunk {
    const left = chunkX * CHUNK_SIZE;
    const top = chunkY * CHUNK_SIZE;
    const width = Math.min(CHUNK_SIZE, this.map.width * this.map.tileSize - left);
    const height = Math.min(CHUNK_SIZE, this.map.height * this.map.tileSize - top);
    const textureKey = this.chunkKey(depth, chunkX, chunkY);
    const texture = this.scene.textures.createCanvas(textureKey, width, height);
    if (!texture)
      throw new Error(`Could not allocate terrain chunk ${textureKey}`);

    const context = texture.getContext();
    context.imageSmoothingEnabled = false;
    context.save();
    context.beginPath();
    context.rect(0, 0, width, height);
    context.clip();
    for (const placement of placements) {
      const source = this.sourceFor(placement);
      context.globalAlpha = placement.opacity;
      context.drawImage(
        source,
        placement.x - left,
        placement.y - top,
        placement.width,
        placement.height,
      );
    }
    context.restore();
    texture.refresh();

    const image = this.scene.add
      .image(left + width / 2, top + height / 2, textureKey)
      .setDepth(depth);
    return { textureKey, image };
  }

  private sourceFor(placement: TerrainPlacement): HTMLImageElement | HTMLCanvasElement {
    const source = this.scene.textures
      .get(`ground-${placement.assetKey}`)
      .getSourceImage() as HTMLImageElement | HTMLCanvasElement;
    if (
      !placement.flipHorizontal &&
      !placement.flipVertical &&
      !placement.flipDiagonal
    )
      return source;

    const key = `${placement.assetKey}:${Number(placement.flipHorizontal)}${Number(placement.flipVertical)}${Number(placement.flipDiagonal)}`;
    const cached = this.transformedSources.get(key);
    if (cached) return cached;

    const asset = this.assetsByKey.get(placement.assetKey)!;
    const canvas = transformTileCanvas(
      source,
      asset.width,
      asset.height,
      placement.flipHorizontal,
      placement.flipVertical,
      placement.flipDiagonal,
    );
    this.transformedSources.set(key, canvas);
    return canvas;
  }

  private clampChunk(index: number, count: number): number {
    return Math.max(0, Math.min(count - 1, index));
  }

  private positionKey(chunkX: number, chunkY: number): string {
    return `${chunkX},${chunkY}`;
  }

  private chunkKey(depth: number, chunkX: number, chunkY: number): string {
    return `map-terrain-${this.map.id}-${depth}-${chunkX}-${chunkY}`;
  }
}
